import React, { FC } from "react"

import { Autocomplete, Button, Drawer, DrawerProps, MultiSelect, Select, Stack } from "@mantine/core"
import { IconFilterX } from "@tabler/icons-react"
import isEqual from "lodash.isequal"

import { DevicesFilterProps } from "./DevicesFilter"
import data from "../services-summary/data"
import { useDevices, useGroups } from "../../../../contexts/devices-context"
import { useLanguage } from "../../../../contexts/language-context"
import { INIT_DEVICES_FILTER } from "../../hooks/useDevicesFilter"
import { colorPerStatus } from "../../hooks/useInferStatus"
import DeviceStatusSelectItem from "./DeviceStatusSelectItem"

type DevicesFilterDrawerProps = DevicesFilterProps & DrawerProps

const DevicesFilterDrawer: FC<DevicesFilterDrawerProps> = ({
    filter,
    setModulesFilter,
    setGroupFilter,
    setStatusFilter,
    setNameFilter,
    resetFilter,
    onClose,
    ...props
}) => {
    const { data: devices } = useDevices()
    const { data: groups } = useGroups()
    const { L } = useLanguage()

    return (
        <Drawer size="16rem" title={L("Filter devices")} onClose={onClose} {...props}>
            <Stack>
                <Autocomplete
                    data={devices.map(device => ({ value: device.id, label: device.name }))}
                    value={filter.name ?? ""}
                    onChange={setNameFilter}
                    label={L("Device name")}
                    placeholder={L("Type to filter")}
                />
                <Select
                    data={groups.map(group => ({ value: group.id, label: group.name }))}
                    value={filter.group}
                    onChange={value => {
                        setGroupFilter(value)
                        onClose()
                    }}
                    label={L("Group name")}
                    placeholder={L("Type to filter")}
                    searchable
                    clearable
                />
                <Select
                    data={Object.keys(colorPerStatus)}
                    value={filter.status}
                    itemComponent={DeviceStatusSelectItem}
                    onChange={value => {
                        setStatusFilter(value)
                        onClose()
                    }}
                    label={L("Status")}
                    placeholder={L("Type to filter")}
                    searchable
                    clearable
                />
                <MultiSelect
                    data={Object.entries(data).map(([key, module]) => ({ value: key, label: module.getLabel(L) }))}
                    value={filter.modules ?? []}
                    onChange={setModulesFilter}
                    label={L("Modules")}
                    placeholder={L("Type to filter")}
                    searchable
                    clearable
                />
                <Button
                    variant="outline"
                    onClick={() => {
                        resetFilter()
                        onClose()
                    }}
                    disabled={isEqual(filter, INIT_DEVICES_FILTER)}
                    leftIcon={<IconFilterX size={20} />}
                >
                    {L("Clear filters")}
                </Button>
            </Stack>
        </Drawer>
    )
}

export default DevicesFilterDrawer
